import * as helper from "@/helpers/index";
import Logger from "@/helpers/logger";
import { dateDiff, dayDiff, isSizeInGB, toGB } from ".";
import { BILLING_MONTHS, ORDER_ACTIVE_STATUSES, ORDER_SUSPENDED_STATUSES } from "./const";
import { isSiteProduct, PRODUCTS } from "./product";

/**
 * Transforms a billing cycle string by replacing underscores with spaces and optionally capitalizing each word.
 *
 * @param {string} billingCycle - The billing cycle string to format.
 * @param {boolean} [shouldCapitalize=false] - Flag indicating whether the first letter of each word should be capitalized.
 * @returns {string} The formatted billing cycle string.
 */
export const getFormattedBillingCycle = (billingCycle, shouldCapitalize = false) => {
  let string = billingCycle.replace(/_/g, " ");

  if (shouldCapitalize) {
    string = string.replace(/\b\w/g, function (match) {
      return match.toUpperCase();
    });
  }

  return string;
};

// checks if the order status is in suspended states
// suspended_pending is also considered as valid suspended state
export const isOrderSuspended = status => ORDER_SUSPENDED_STATUSES.includes(status);

// checks if the order status is in active states
// pending is also considered as active state, app should work as usual only email sending/receiving will not work
export const isOrderActive = status => ORDER_ACTIVE_STATUSES.includes(status);

/**
 *
 * @param {String[]} billingCycles
 */
export const sortBillingCycles = billingCycles => {
  if (!billingCycles) {
    return billingCycles;
  }

  return billingCycles.sort((a, b) => {
    const a1 = BILLING_MONTHS[a] || Number.MAX_SAFE_INTEGER;
    const a2 = BILLING_MONTHS[b] || Number.MAX_SAFE_INTEGER;
    return a1 - a2;
  });
};

export const getMinEmailAccountCount = (selectedRecord, currentPlan) => {
  if (!currentPlan.mailSuiteDetails?.planId) return 1;

  const upgradeToSamePlan =
    currentPlan.mailSuiteDetails.planDetails.isTrial &&
    selectedRecord.id === currentPlan.mailSuiteDetails.paidPlan.id;
  const minCount = upgradeToSamePlan
    ? currentPlan.mailSuiteDetails.noOfAccounts.limit + 1 // +1 to avoid 0 billing total
    : (currentPlan.mailSuiteDetails.planDetails.isPaid
        ? currentPlan.mailSuiteDetails.noOfAccounts.purchased
        : currentPlan.mailSuiteDetails.noOfAccounts.active) || 1; // default 1
  return minCount;
};

export const getPlanFeatureOrder = product => {
  const mailFeatureOrder = {
    storage: 1,
    mobile_apps: 2,
    web_mail: 3,
    calendar: 4,
    read_receipts: 5,
    video_calling: 6,
  };

  // FIXME: set feat order for site
  return isSiteProduct(product) ? mailFeatureOrder : mailFeatureOrder;
};

export const getPlanRemainingDaysAndMonths = expiresOn => {
  const remainingDays = dayDiff(expiresOn);
  const remainingMonths = dateDiff(new Date(), expiresOn);

  // < 1.5 month = 2month
  if (remainingMonths.monthDiff >= 1 && remainingMonths.dayDiff > 15) {
    remainingMonths.monthDiff++;
  }

  return {
    remainingDays,
    remainingMonths,
  };
};

export const processFeatureAttributes = f => {
  switch (f.type) {
    case "storage":
      f.attrs = { ...f.attrs }; // create new copy of attr and then update, do not update original data
      const quota = parseInt(f.attrs.quota_in_mb, 10);
      const convertedQuota = (f.attrs.quota_in_gb || toGB(f.attrs.quota_in_mb)).toString();
      if (isSizeInGB(quota)) {
        f.attrs.quota_in_gb = convertedQuota;
        delete f.attrs.quota_in_mb;
      } else {
        f.attrs.quota_in_mb = convertedQuota;
      }
      break;

    // Future cases for other feature types can be added here
    default:
      break;
  }

  return f;
};

export const getMailSuiteDetailsFromCurrentPlan = currentPlan => {
  const mailSuiteDetails = currentPlan?.orderDetails?.[PRODUCTS.MAIL_SUITE];
  const isLatestAPIResp = currentPlan?.orderDetails;

  if (isLatestAPIResp && !mailSuiteDetails) return { mailSuiteDetails: {} };

  if (!isLatestAPIResp && !mailSuiteDetails) {
    // to handle API backward compatibility
    return {
      mailSuiteDetails: {
        addons: currentPlan.addons,
        amountToBePaidOnRenewal: currentPlan.amountToBePaidOnRenewal,
        expiry: currentPlan.expiresOn,
        flags: currentPlan.domainFlags,
        noOfAccounts: currentPlan.noOfAccounts,
        planId: currentPlan.planId,
        product: PRODUCTS.MAIL_SUITE,
      },
    };
  }

  return {
    mailSuiteDetails: {
      addons: currentPlan.addons,
      amountToBePaidOnRenewal: mailSuiteDetails?.totalRenewalCharge,
      createdAt: mailSuiteDetails?.createdAt,
      expiry: mailSuiteDetails?.expiry,
      flags: mailSuiteDetails?.flags,
      isProductSuspended: isOrderSuspended(mailSuiteDetails?.status),
      noOfAccounts: mailSuiteDetails?.accountQuota,
      orderId: mailSuiteDetails?.id,
      planId: mailSuiteDetails?.planId,
      pricingConfigId: mailSuiteDetails.pricingId,
      product: PRODUCTS.MAIL_SUITE,
    },
  };
};

export const getSiteDetailsFromCurrentPlan = currentPlan => {
  const siteDetails = currentPlan?.orderDetails?.[PRODUCTS.SITE];

  // no need to check isLatestAPIResp, as in old flow we never had plans and plan id for site, hence empty
  if (!siteDetails) return { siteDetails: {} };

  return {
    siteDetails: {
      orderId: siteDetails?.id,
      planId: siteDetails?.planId,
      amountToBePaidOnRenewal: siteDetails?.totalRenewalCharge,
      flags: siteDetails?.flags,
      createdAt: siteDetails?.createdAt,
      addons: [],
      product: PRODUCTS.SITE,
      isProductSuspended: isOrderSuspended(siteDetails?.status),
      expiry: siteDetails?.expiry,
      pricingConfigId: siteDetails.pricingId,
    },
  };
};

export const getDomainDetailsFromCurrentPlan = currentPlan => {
  const domainDetails = currentPlan?.orderDetails?.[PRODUCTS.DOMAIN];
  const isLatestAPIResp = currentPlan?.orderDetails;

  if (isLatestAPIResp && !domainDetails) return { domainDetails: {} };

  if (
    !isLatestAPIResp &&
    !domainDetails &&
    Object.keys(currentPlan.domainPricingAttr || {}).length
  ) {
    // this is for old flow, where we used to derived isPaidDomain based on `domainPricingAttr`
    // if domainPricingAttr that means we need to keep the domain details
    return {
      domainDetails: {
        addons: [],
        amountToBePaidOnRenewal: currentPlan.totalDomainCharge,
        createdAt: null,
        expiry: currentPlan.expiresOn,
        flags: currentPlan.domainFlags,
        orderId: null, // should be domain Id
        planId: currentPlan.planId,
        pricingAttr: currentPlan.domainPricingAttr,
        product: PRODUCTS.DOMAIN,
      },
    };
  }

  return {
    domainDetails: {
      addons: [],
      amountToBePaidOnRenewal: domainDetails.totalRenewalCharge,
      createdAt: domainDetails?.createdAt,
      expiry: domainDetails?.expiry,
      flags: domainDetails.flags,
      isProductSuspended: isOrderSuspended(domainDetails?.status),
      orderId: domainDetails?.id,
      planId: domainDetails.planId,
      pricingAttr: domainDetails.pricingAttr,
      pricingConfigId: domainDetails.pricingId,
      product: PRODUCTS.DOMAIN,
    },
  };
};

export const getPromoDiscountInfo = (promoCode, promoCodeDiscount) => {
  if (!promoCodeDiscount || !promoCode) return null;

  const totalNumberOfProducts = Object.keys(PRODUCTS).length;
  const discounts = promoCode.config?.discounts;

  const isBundleLevelDiscount = Object.keys(discounts)?.length === totalNumberOfProducts;

  let discount = {},
    discountedProduct = null;

  /**
  The discount applies either to all products (bundle) or to a single specific product.
  There's no scenario where the discount applies to multiple (but not all) products
   */

  if (isBundleLevelDiscount) {
    discount = {
      type: "percentage",
      value:
        (discounts?.email_account?.value + discounts?.site?.value + discounts?.domain?.value) /
        totalNumberOfProducts,
    };
  } else {
    if (discounts?.email_account) {
      discountedProduct = "Mail";
      discount = discounts?.email_account;
    } else if (discounts?.site) {
      discountedProduct = "Site";
      discount = discounts?.site;
    } else if (discounts?.domain) {
      discountedProduct = "Domain";
      discount = discounts?.domain;
    }
  }

  return {
    id: promoCode.id,
    code: promoCode.displayCode,
    value: helper.parseDiscountConfig(discount),
    expireInDays: helper.calculateDateDifference(promoCode.expiryDate),
    amount: promoCodeDiscount,
    isBundleLevelDiscount,
    discountedProduct,
  };
};

/**
 * Applies pricing configuration to plans based on the provided config
 * @param {Array} plans - Array of plans to apply pricing config to
 * @param {Object|null} pricingConfigs - Pricing configuration response from API
 * @param {string} orderType - Type of order (MAIL_SUITE, SITE, DOMAIN)
 * @param {string} currency - Currency code for pricing
 * @param {number|null} pricingConfigId - Pricing configuration ID from current plan
 * @returns {Array} Plans with updated pricing or original plans if config not found
 */
export const applyPricingConfig = (plans, pricingConfigs, orderType, pricingConfigId) => {
  if (!pricingConfigs?.pricings?.length || !plans?.length || !pricingConfigId) {
    return plans;
  }

  const pricingConfig = pricingConfigs.pricings.find(config => config.id === pricingConfigId);

  if (!pricingConfig?.config?.planTypeToPricingConfig) {
    Logger.error(
      `Pricing config ${pricingConfigId} not found for ${orderType} plans - treating as critical`
    );
    throw new Error(`Pricing config ${pricingConfigId} not found for ${orderType} plans`);
  }

  return plans.map(plan => {
    const planConfig = pricingConfig.config.planTypeToPricingConfig[plan.type];
    if (!planConfig) {
      return plan;
    }

    const currencyPricing = planConfig.pricingList.find(p => p.currency === plan.currency);
    if (!currencyPricing) {
      Logger.warn(
        `Currency ${plan.currency} not found for plan type ${plan.type} in config ${pricingConfigId}`
      );
      return {
        ...plan,
        pricingConfigId: pricingConfigId,
      };
    }

    return {
      ...plan,
      pricingConfigId: pricingConfigId,
      planPricing: {
        ...plan.planPricing,
        ...currencyPricing.billingCyclePlanPriceMap,
      },
    };
  });
};

/**
 * Processes plans with pricing configuration for multiple order types
 * Fetches pricing configs once and applies them to all order types for optimal performance
 * @param {Object} plansGroupedByOrderType - Plans grouped by order type
 * @param {Object} pricingConfigIds - Pricing configuration IDs from current plan
 * @param {Object} pricingConfigs - Pricing configuration response from API
 * @returns {Object} Plans with applied pricing configuration grouped by order type
 */
export const processPlansWithPricingConfig = (
  plansGroupedByOrderType,
  pricingConfigIds,
  pricingConfigs
) => {
  const processedPlans = {};

  // Check if pricing configs are available
  if (!pricingConfigs) {
    Logger.warn("No pricing configs provided, using default pricing");
    // Return original plans if pricing configs are not available
    return plansGroupedByOrderType;
  }

  // Process for each order type
  Object.keys(plansGroupedByOrderType).forEach(orderType => {
    const plans = plansGroupedByOrderType[orderType];
    if (!plans?.length) {
      processedPlans[orderType] = plans;
      return;
    }

    const pricingConfigId = pricingConfigIds[orderType];
    processedPlans[orderType] = applyPricingConfig(
      plans,
      pricingConfigs,
      orderType,
      pricingConfigId
    );
  });
  return processedPlans;
};
